import React from "react";
import Box from "@mui/material/Box";
import Card from "@mui/material/Card";
import CardContent from "@mui/material/CardContent";
import Typography from "@mui/material/Typography";
import Button from "@mui/material/Button";
import Chip from "@mui/material/Chip";
import Divider from "@mui/material/Divider";
import useMediaQuery from "@mui/material/useMediaQuery";
import { useNavigate } from "react-router-dom";
import { supabase } from "../utils/supabase";
import { useTranslation } from "../utils/i18n";

export default function NewsComponent({ items, limit = 3, mobileLimit, showCTA = true }) {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const isMobile = useMediaQuery("(max-width:599px)");
  const [fetchedItems, setFetchedItems] = React.useState([]);
  const [isLoading, setIsLoading] = React.useState(false);

  React.useEffect(() => {
    if (Array.isArray(items)) return;

    let isMounted = true;

    const fetchNewsletters = async () => {
      setIsLoading(true);

      const { data } = await supabase
        .from("newsLetter")
        .select("id, created_at, title, description, type, image_url")
        .order("created_at", { ascending: false });

      if (!isMounted) return;
      setFetchedItems(Array.isArray(data) ? data : []);
      setIsLoading(false);
    };

    fetchNewsletters();

    return () => {
      isMounted = false;
    };
  }, [items]);

  const source = Array.isArray(items) ? items : fetchedItems;
  const activeLimit = isMobile && mobileLimit != null ? mobileLimit : limit;
  const list = source.slice(0, activeLimit).map((item) => ({
    id: item.id,
    tag: item.type || "Nyhet",
    title: item.title,
    excerpt: item.description || "-",
    imageUrl: item.image_url || "",
    date: item.created_at
      ? new Date(item.created_at).toLocaleDateString("sv-SE")
      : "-",
  }));

  return (
    <Box
      sx={{
        width: "100%",
        py: { xs: 6, md: 10 },
        px: { xs: 2, sm: 4, md: "var(--site-gutter)" },
        maxWidth: "var(--content-max-width)",
        mx: "auto",
        boxSizing: "border-box",
      }}
    >
      <Box sx={{ textAlign: "center", mb: 4 }}>
        <Typography variant="overline" sx={{ letterSpacing: 2 }}>
          {t("newsSection.overline")}
        </Typography>
        <Typography variant="h4" sx={{ fontWeight: 700, mt: 1 }}>
          {t("newsSection.title")}
        </Typography>
      </Box>

      {isLoading ? (
        <Typography color="text.secondary" sx={{ textAlign: "center" }}>
          {t("newsSection.loading")}
        </Typography>
      ) : (
        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: {
              xs: "1fr",
              sm: "repeat(2, 1fr)",
              md: "repeat(3, 1fr)",
            },
            gap: 3,
          }}
        >
          {list.map((n) => (
            <Card
              key={n.id}
              sx={{
                borderRadius: 2,
                boxShadow: "0 10px 30px rgba(0,0,0,0.06)",
                overflow: "hidden",
                display: "flex",
                flexDirection: "column",
                cursor: "pointer",
                width: "100%",
              }}
              onClick={() => navigate(`/news/${n.id}`)}
            >
              {/* Image */}
              <Box
                sx={{
                  position: "relative",
                  width: "100%",
                  height: { xs: 180, sm: 190 },
                  bgcolor: "#E8EEF5",
                  backgroundImage: n.imageUrl ? `url(${n.imageUrl})` : "none",
                  backgroundSize: "cover",
                  backgroundPosition: "center",
                  flexShrink: 0,
                }}
              >
                <Chip
                  label={n.tag}
                  size="small"
                  sx={{
                    position: "absolute",
                    left: 12,
                    top: 12,
                    bgcolor: "rgba(0,0,0,0.7)",
                    color: "#fff",
                  }}
                />
              </Box>

              {/* Content */}
              <CardContent
                sx={{
                  flex: "1 1 auto",
                  display: "flex",
                  flexDirection: "column",
                }}
              >
                <Typography
                  variant="h6"
                  sx={{
                    fontWeight: 700,
                    mb: 1,
                    overflow: "hidden",
                    display: "-webkit-box",
                    WebkitLineClamp: 2,
                    WebkitBoxOrient: "vertical",
                  }}
                >
                  {n.title}
                </Typography>
                <Typography
                  variant="body2"
                  color="text.secondary"
                  sx={{
                    mb: 2,
                    overflow: "hidden",
                    display: "-webkit-box",
                    WebkitLineClamp: 2,
                    WebkitBoxOrient: "vertical",
                  }}
                >
                  {n.excerpt}
                </Typography>
                <Box sx={{ mt: "auto" }}>
                  <Divider />
                  <Typography
                    variant="caption"
                    color="text.secondary"
                    sx={{ display: "block", mt: 1.5 }}
                  >
                    {n.date}
                  </Typography>
                </Box>
              </CardContent>
            </Card>
          ))}
        </Box>
      )}

      {showCTA && (
        <Box sx={{ textAlign: "center", mt: 6 }}>
          <Button
            variant="contained"
            sx={{ backgroundColor: "#FF6A00", borderRadius: 6, px: 4 }}
            onClick={() => navigate("/news")}
          >
            {t("newsSection.cta")}
          </Button>
        </Box>
      )}
    </Box>
  );
}
